import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import Observable from 'zen-observable-ts';

import {
  OnUpdateAudioLibraryItemSubscription,
  OnCreateAudioLibraryItemSubscription,
  OnDeleteAudioLibraryItemSubscription,
} from '../../graphql/api';
import {
  addToAudioLibrary,
  deleteFromAudioLibrary,
  updateAudioLibrary,
  updateSubscription,
} from '../../actions';
import { IState, Subscription } from '../../graphql/types';

const onCreateAudioLibraryItem = gql`
  subscription OnCreateAudioLibraryItem($userID: String) {
    onCreateAudioLibraryItem(userID: $userID) {
      id
      audioUrl
      createdAt
      durationSeconds
      tenantID
      title
      updatedAt
      userID
      state
    }
  }
`;

const onUpdateAudioLibraryItem = gql`
  subscription OnUpdateAudioLibraryItem($userID: String) {
    onUpdateAudioLibraryItem(userID: $userID) {
      id
      state
      audioUrl
    }
  }
`;

const onDeleteAudioLibraryItem = gql`
  subscription OnDeleteAudioLibraryItem($userID: String) {
    onDeleteAudioLibraryItem(userID: $userID) {
      id
    }
  }
`;

const useAudioLibrarySubscription = (userId?: string) => {
  const subUpdate = useSelector<IState, Subscription | undefined>(
    state => state.subscriptions.updateAudioLibrary
  );
  const subCreate = useSelector<IState, Subscription | undefined>(
    state => state.subscriptions.createAudioLibrary
  );
  const subDelete = useSelector<IState, Subscription | undefined>(
    state => state.subscriptions.deleteAudioLibrary
  );

  const dispatch = useDispatch();

  const subscribeCreate = useCallback(() => {
    if (!userId) {
      return;
    }
    if (subCreate) {
      subCreate.unsubscribe();
    }
    const createObs = API.graphql(
      graphqlOperation(onCreateAudioLibraryItem, {
        userID: userId,
      })
    );
    if (createObs instanceof Observable) {
      const createSub = createObs.subscribe({
        next: (result: {
          value: { data: OnCreateAudioLibraryItemSubscription };
        }) => {
          const input = result.value.data.onCreateAudioLibraryItem;
          if (input && input.id) {
            dispatch(addToAudioLibrary(input));
          }
        },
        complete: () => console.log('completed audio library subscription'),
        error: console.log,
      });
      dispatch(updateSubscription('createAudioLibrary', createSub));
    }
  }, [subCreate, userId, dispatch]);

  const subscribeUpdate = useCallback(() => {
    if (!userId) {
      return;
    }
    if (subUpdate) {
      subUpdate.unsubscribe();
    }
    const updateObs = API.graphql(
      graphqlOperation(onUpdateAudioLibraryItem, {
        userID: userId,
      })
    );
    if (updateObs instanceof Observable) {
      const updateSub = updateObs.subscribe({
        next: (result: {
          value: { data: OnUpdateAudioLibraryItemSubscription };
        }) => {
          const input = result.value.data.onUpdateAudioLibraryItem;
          if (input && input.id) {
            dispatch(updateAudioLibrary(input));
          }
        },
        complete: () => console.log('completed audio library subscription'),
        error: console.log,
      });
      dispatch(updateSubscription('updateAudioLibrary', updateSub));
    }
  }, [subUpdate, userId, dispatch]);

  const subscribeDelete = useCallback(() => {
    if (!userId) {
      return;
    }
    if (subDelete) {
      subDelete.unsubscribe();
    }
    const deleteObs = API.graphql(
      graphqlOperation(onDeleteAudioLibraryItem, {
        userID: userId,
      })
    );
    if (deleteObs instanceof Observable) {
      const deleteSub = deleteObs.subscribe({
        next: (result: {
          value: { data: OnDeleteAudioLibraryItemSubscription };
        }) => {
          const id = result.value.data.onDeleteAudioLibraryItem?.id;

          if (id) {
            dispatch(deleteFromAudioLibrary(id));
          }
        },
        complete: () => console.log('completed audio library subscription'),
        error: console.log,
      });
      dispatch(updateSubscription('deleteAudioLibrary', deleteSub));
    }
  }, [subDelete, userId, dispatch]);

  return { subscribeUpdate, subscribeCreate, subscribeDelete };
};

export default useAudioLibrarySubscription;
